import React, { useState, useEffect, useRef } from "react"
import { graphql } from '@apollo/client/react/hoc'
import {
    Button,
    Form,
    FormText,
    FormGroup,
    Label,
    Input,
    Popover,
    PopoverHeader,
    PopoverBody,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    Progress,
} from 'reactstrap'
import { ActionTypesAndParamsQuery, ActionQuery, AvailableModelQuery } from "./Queries"
import Select from "react-select"
import { UpdateActionMutation, CreateActionMutation } from "./Mutations"
import withApolloClient from '../withApolloClient'

const ActionForm = ({ actionTypesAndParams, action, availableModel, handleClose, isOpen, isUpdate, loading, updateAction, createAction, actionId, automationId, refetchQuery, refetchAutomationQuery }) => {

    // value of the action type and param 
    const [actionValue, setActionValue] = useState({
        type: action && action.actionType || "",
        params: action && action.actionParams && JSON.parse(action.actionParams.replace(/'/g, '"')) || [],
    })
    // value of the action query
    const [error, setError] = useState(null)
    const [stateloading, setStateLoading] = useState(false)

    const [actionParamsValues, setActionParamsValues] = useState({})
    const [openModal, setOpenModal] = useState("")
    const [currentCursorPosition, setCurrentCursorPosition] = useState(0)
    const inputRefs = useRef({})

    const validateForm = () => {
        const newErrors = ""
        Object.keys(actionParamsValues).forEach((key) => {
            return actionParamsValues[key] === '' ? newErrors = newErrors + ` ${key} field is required. \n` : null
        })
        setError(newErrors)
        return newErrors.length === 0
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        setStateLoading(true)
        if (validateForm()) {
            if (isUpdate) {

                updateAction({
                    variables: {
                        input: {
                            id: actionId,
                            actionType: action.type,
                            actionParams: JSON.stringify(actionParamsValues)
                        },
                    },
                }).then(({ data }) => {
                    if (data.updateAction.ok) {
                        handleClose()
                        refetchQuery()
                    } else {
                        setError(data.updateAction.errors[0].messages[0])
                    }
                    setStateLoading(false)
                })
            } else {
                createAction({
                    variables: {
                        input: {
                            automationId,
                            actionType: actionValue.type,
                            actionParams: JSON.stringify(actionParamsValues),
                        }
                    }
                }).then(({ data }) => {
                    if (data.createAction.ok) {
                        handleClose()
                        refetchAutomationQuery()
                    } else {
                        setError(data.createAction.errors[0].messages[0])
                    }
                    setStateLoading(false)
                })
            }
        }
    }


    const handleActionParamChange = (field, value, append) => {
        const newActionParamsValues = { ...actionParamsValues }
        newActionParamsValues[field] = append && newActionParamsValues[field] ? `${newActionParamsValues[field]}${value}` : value
        setActionParamsValues(newActionParamsValues)
    }
    const handleInstanceFieldClick = (paramName, field) => {
        let currentParamValues = Object.keys(actionParamsValues).some((key) => {
            return actionParamsValues[key] !== ''
        })
        let paramValues = isUpdate && !currentParamValues ? JSON.parse(action.actionParams.replace(/'/g, '"')) : actionParamsValues
        const newActionParamsValues = { ...paramValues }
        const inputRef = inputRefs.current[paramName]
        const value = newActionParamsValues[paramName]
        const newValue = `${value.slice(0, currentCursorPosition)}{{instance.${field}}}${value.slice(currentCursorPosition)}`
        newActionParamsValues[paramName] = newValue
        setActionParamsValues(newActionParamsValues)
        inputRef.focus()
        inputRef.setSelectionRange(currentCursorPosition + 11, currentCursorPosition + 11 + field.length)
    }

    useEffect(() => {
        if (action && actionTypesAndParams && actionTypesAndParams.length > 0) {
            let selectedActionTypeAndParam = actionTypesAndParams.filter((item) => item.actionType === action.actionType.toLowerCase())[0]
            setActionValue({ ...actionValue, type: selectedActionTypeAndParam.actionType.toUpperCase(), params: JSON.parse(selectedActionTypeAndParam.actionParams) })
            if (isUpdate && action.actionParams) {
                setActionParamsValues(JSON.parse(action.actionParams.replace(/'/g, '"')))
            }
        }
    }, [actionTypesAndParams, action])

    useEffect(() => {
        // When actionTypes changes, set default values for actionParams
        if (!isUpdate && actionValue && actionValue.type && actionValue.params && actionValue.params.length > 0) {
            const defaultActionParams = {}
            actionValue.params.forEach((param) => {
                defaultActionParams[param.name] = param.default || ''
            })
            setActionParamsValues(defaultActionParams)
        }
    }, [actionValue])
    return (
        <Modal isOpen={isOpen} toggle={handleClose}>
            <ModalHeader toggle={handleClose}>Action Form</ModalHeader>
            <ModalBody>
                {error && <div className="alert alert-danger">{error}</div>}
                {(loading || stateloading) && <Progress animated color="info" value="100" />}
                <Form className="ActionForm" onSubmit={handleSubmit}>
                    <div>
                        <label>Action Type</label>
                        <Select className="bos-custom-select"
                            classNamePrefix="bos-select" isLoading={false}
                            options={
                                actionTypesAndParams && actionTypesAndParams.map((item) => ({
                                    label: item.actionType ? item.actionType.replaceAll('_', ' ').toUpperCase() : '',
                                    value: item,
                                }))
                            }
                            value={actionValue && actionValue.type && {
                                label: actionValue.type.replaceAll('_', ' ').toUpperCase(),
                                value: action,
                            }}
                            isDisabled={isUpdate}
                            placeholder="Select Action Type"
                            onChange={(option) => {
                                setActionValue({ ...actionValue, type: option.value.actionType, params: JSON.parse(option.value.actionParams) })
                            }}
                        />
                    </div>
                    <br />
                    {actionValue && actionValue.type && actionValue.params && Array.isArray(actionValue.params) && actionValue.params.length > 0 && (
                        <div>
                            {actionValue.params.map((param) => (
                                <FormGroup key={param.name}>
                                    <Label for={param.name}>{param.name.toUpperCase()}{param.is_required && " *"}</Label>
                                    {param.description && <FormText color="muted">{param.description}</FormText>}
                                    {param.input === 'input' ? (
                                        <Input
                                            type="text"
                                            id={param.name + "actionParam"}
                                            defaultValue={action ? actionParamsValues[param.name] : ''}
                                            value={actionParamsValues && actionParamsValues[param.name]}
                                            required={param.is_required}
                                            onChange={(e) => handleActionParamChange(param.name, e.target.value)}
                                            onFocus={(e) => setOpenModal(`Popover${param.name}`)}
                                            onKeyUp={(e) => { setCurrentCursorPosition(e.target.selectionStart) }}
                                            innerRef={(el) => inputRefs.current[param.name] = el}
                                        />
                                    ) : param.input === 'textarea' ? (
                                        <Input
                                            type="textarea"
                                            id={param.name + "actionParam"}
                                            defaultValue={action ? actionParamsValues[param.name] : ''}
                                            value={actionParamsValues && actionParamsValues[param.name]}
                                            required={param.is_required}
                                            onChange={(e) => handleActionParamChange(param.name, e.target.value)}
                                            onFocus={(e) => setOpenModal(`Popover${param.name}`)}
                                            onKeyUp={(e) => { setCurrentCursorPosition(e.target.selectionStart) }}
                                            innerRef={(el) => inputRefs.current[param.name] = el}
                                        />
                                    ) : param.input === 'select' ? (
                                        <Input
                                            type="select"
                                            id={param.name + "actionParam"}
                                            value={actionParamsValues[param.name]}
                                            required={param.is_required}
                                            onChange={(e) => handleActionParamChange(param.name, e.target.value)}
                                        >
                                            {param.choices.map((choice) => (
                                                <option key={choice} value={choice}>{choice}</option>
                                            ))}
                                        </Input>
                                    ) : param.input === 'date' ? (
                                        <Input
                                            type="date"
                                            id={param.name + "actionParam"}
                                            defaultValue={action ? actionParamsValues[param.name] : ''}
                                            required={param.is_required}
                                            onChange={(e) => handleActionParamChange(param.name, e.target.value)}
                                            onKeyUp={(e) => { setCurrentCursorPosition(e.target.selectionStart) }}
                                            innerRef={(el) => inputRefs.current[param.name] = el}
                                        />
                                    ) : null}

                                    {availableModel && <Popover placement="bottom" target={param.name + "actionParam"} isOpen={openModal === `Popover${param.name}`} toggle={() => setOpenModal('')}>
                                        <PopoverHeader>Populate from {availableModel && `${availableModel.contentType.model}`} fields</PopoverHeader>
                                        <PopoverBody>
                                            {availableModel && availableModel.availableModelFields.map((field) => (
                                                <Badge
                                                    className="text-dark m-2 p-2" key={field}
                                                    style={{ cursor: "pointer", fontSize: 10 }} color="warning"
                                                    onClick={() => handleInstanceFieldClick(param.name, field)}>
                                                    {field.replaceAll('_', ' ').toUpperCase()}
                                                </Badge>
                                            ))}
                                        </PopoverBody>
                                    </Popover>}
                                </FormGroup>
                            ))}
                        </div>
                    )}
                    {loading && <Progress animated color="info" value="100" />}
                    <Button type="submit" className="float-right" disabled={!availableModel}>Submit</Button>
                </Form>

            </ModalBody>
        </Modal>
    )
}

export default graphql(ActionTypesAndParamsQuery, {
    options: () => ({
        variables: {},
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network'
    }),
    props: ({ data: { actionTypesAndParams } }) => ({ actionTypesAndParams }),
})(
    graphql(ActionQuery, {
        options: ({ actionId }) => ({ variables: { id: actionId } }),
        skip: ({ actionId }) => !actionId,
        props: ({ data: { action, loading, refetch, variables } }) => ({
            action,
            loading,
            variables,
            refetchQuery: () => {
                return refetch({
                    query: ActionQuery,
                    variables: { ...variables },
                    updateQuery: (previousResult, { fetchMoreResult }) => ({
                        action: fetchMoreResult.action,
                    }),
                })
            },
        }),
    })(
        graphql(AvailableModelQuery, {
            options: ({ contentTypeId }) => ({
                variables: { contentTypeId },
                notifyOnNetworkStatusChange: true,
                fetchPolicy: 'cache-and-network',
            }),
            props: ({ data: { availableModel } }) => ({ availableModel }),
        })(
            graphql(CreateActionMutation, { name: 'createAction' })(
                graphql(UpdateActionMutation, { name: 'updateAction' })(
                    withApolloClient(ActionForm)
                )
            )
        )
    )
)
